import React, { ReactNode, useEffect, useRef } from 'react';

import { useController } from 'react-hook-form';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { SettingsComponent } from '../../lib/declarations/EditorComponentTypes';
import { BlockSpecificPage } from '../../lib/declarations/BlockSpecificPage';
import { useCurrentBlock } from '../CurrentBlockProvider';
import ViewTypeSelector from '../../lib/components/settings/ViewTypeSelector';
import { PageListBlock, PageListViewType } from '../../../declarations/models/blocks/PageListBlocks';

/* eslint-disable @typescript-eslint/no-explicit-any */
const usePrevious = <T,>(value: T): T | undefined => {
  const ref = useRef<T | undefined>(undefined);

  useEffect(() => {
    ref.current = value;
  });

  return ref.current;
};
/* eslint-enable @typescript-eslint/no-explicit-any */

const VARIANT_ICONS: Record<PageListViewType, ReactNode> = {
  [PageListViewType.GRID]: <MaterialSymbol name='view_module' />,
  [PageListViewType.LIST]: <MaterialSymbol name='view_list' />,
  [PageListViewType.SIMPLE]: <MaterialSymbol name='view_headline' fill />,
};

export const PageBlockViewTypeSelector: SettingsComponent = () => {
  const { blockPath, block } = useCurrentBlock();
  const currentBlock = block as PageListBlock;

  const previousSelectedViewType = usePrevious(currentBlock.view);

  const {
    field: { value: selectedViewType, onChange: setSelectedViewType },
  } = useController<BlockSpecificPage<PageListBlock>, `${typeof blockPath}.view`>({ name: `${blockPath}.view` });

  const {
    field: { onChange: setShowDescription },
  } = useController<BlockSpecificPage<PageListBlock>, `${typeof blockPath}.displayDescription`>({
    name: `${blockPath}.displayDescription`,
  });

  useEffect(() => {
    if (previousSelectedViewType !== undefined && selectedViewType !== previousSelectedViewType) {
      if (selectedViewType === PageListViewType.SIMPLE && previousSelectedViewType !== PageListViewType.SIMPLE) {
        setShowDescription(false);
      }
    }
  }, [selectedViewType, setShowDescription, previousSelectedViewType]);

  return (
    <ViewTypeSelector
      viewType={selectedViewType}
      viewTypes={Object.values(PageListViewType)}
      onChange={setSelectedViewType}
      variantIcons={VARIANT_ICONS}
      variantTranslationPath='PageListViewTypeSelector'
    />
  );
};

export default PageBlockViewTypeSelector;
